import { forwardRef } from 'react'
import styles from './ScoopMenu.module.scss'

/**
 * ScoopMenuItem - Individual menu item with support for icons, text, and badges
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} [props.leftSection] - Content on the left (typically an icon)
 * @param {React.ReactNode} [props.rightSection] - Content on the right (typically a badge/chip)
 * @param {string} [props.primary] - Primary text (bold, main content)
 * @param {string} [props.secondary] - Secondary text (muted, description)
 * @param {Function} [props.onClick] - Click handler
 * @param {boolean} [props.selected=false] - Whether item is selected
 * @param {boolean} [props.disabled=false] - Whether item is disabled
 * @param {number|string} [props.height] - Custom height (e.g., 60, "60px")
 * @param {boolean} [props.framedIcon=false] - If true, wraps leftSection icon in a colored frame/container
 * @param {string} [props.className] - Additional CSS classes
 * @param {Object} [props.style] - Inline styles for override
 * @param {React.ReactNode} [props.children] - Custom content (used if primary/secondary not provided)
 * @param {React.Ref} ref - Forwarded ref to underlying element
 */
export const ScoopMenuItem = forwardRef(
    (
        {
            leftSection,
            rightSection,
            primary,
            secondary,
            onClick,
            selected = false,
            disabled = false,
            height,
            framedIcon = false,
            className = '',
            style,
            children,
            ...props
        },
        ref
    ) => {
        const itemClassName = [
            styles.menuItem,
            selected && styles.selected,
            disabled && styles.disabled,
            className,
        ]
            .filter(Boolean)
            .join(' ')

        const handleClick = (event) => {
            if (disabled) return
            onClick?.(event)
        }

        // Combine height prop with existing styles
        const itemStyle = {
            ...(height && {
                minHeight: typeof height === 'number' ? `${height}px` : height,
            }),
            ...style,
        }
        // Wrap leftSection in icon container if framedIcon is true
        const renderedLeftSection = leftSection && (
            <span className={styles.leftSection}>
                {framedIcon ? (
                    <span className={styles.iconContainer}>{leftSection}</span>
                ) : (
                    leftSection
                )}
            </span>
        )

        return (
            <div
                ref={ref}
                className={itemClassName}
                onClick={handleClick}
                role="menuitem"
                aria-disabled={disabled}
                aria-current={selected ? 'true' : undefined}
                tabIndex={disabled ? -1 : 0}
                {...props}
                style={itemStyle}
            >
                {renderedLeftSection}

                {primary || secondary ? (
                    <div className={styles.content}>
                        {primary && <span className={styles.primary}>{primary}</span>}
                        {secondary && <span className={styles.secondary}>{secondary}</span>}
                    </div>
                ) : (
                    <div className={styles.content}>{children}</div>
                )}

                {rightSection && <span className={styles.rightSection}>{rightSection}</span>}
            </div>
        )
    }
)

// This is useful for debugging and React DevTools
ScoopMenuItem.displayName = 'ScoopMenuItem'
